import { useCallback, useEffect, useState } from "react";
import {
    Box,
    CircularProgress,
    Divider,
    IconButton,
    Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

import {
    CancelFormActionButton,
    PrimaryFormActionButton,
} from "../../../Features/Table/GenericDraggableTable/TableStyles";
import { getNetworkEntity } from "../../../Api/NetworkEntityApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { formatCellForCSV } from "./tableHelpers";
import { FORM_TOKENS } from "../WorkOrders/WorkOrderFormShared";
import AssetsTabContent from "../Assets/AssetsTabContent";
import { heldAssetsEmptyHint, heldAssetsHeading } from "./entityPageHelpers";

// Full-page detail for one network entity.
//
// Same component as the popup in every respect that matters -- the fields come
// from the entity type's `properties`, and values go through the formatter the
// table and the CSV use -- but it has room for LINKED RECORDS, which is the
// whole reason a Customer needs a page rather than a dialog: one customer has
// many batteries, and each battery carries its own loan ID.
//
// A PAGE, not a dialog. It began as a FullScreenDialog, which is the module's
// convention for a detail view opened from the row that lists it -- but this
// one is opened by a URL, from two places, and a slide-over that covers the
// nav is the wrong shape for that: it dims the sidebar the user still needs,
// it carries an X whose destination nobody can guess, and reloading the URL
// animates a panel over a page that was never there.
//
// So it renders inside the app shell like any other page, and it LEAVES by a
// Back button that names where it goes -- the Network sub-tab the entity
// belongs to, not just "the list". NetworkEntityPage mounts it at
// /asset-management/network/:entityID, which is what gives the Assets tab's
// Current Custody cell somewhere to link to.
//
// EVERY type gets this. `layout` is no longer read -- it was opt-in and no org
// ever set it, so every entity opened in a six-field dialog instead. What is
// still config is WHICH linked sections a type shows:
//
//   network.detailViews[<entityTypeCode>] = {
//       "sections": [{
//           "key": "batteries",
//           "title": "Batteries & Loans",
//           "dataKey": "batteries",
//           "emptyMessage": "No batteries are linked to this customer yet.",
//           "columns": [{ "key": "batteryID", "label": "Battery ID" }, ...]
//       }]
//   }
//
// A section renders whatever the detail response puts under `dataKey`. Nothing
// here fabricates rows: until the API returns that key the section shows its
// empty state, and it lights up with no frontend change once it does.

const DISPLAY_KEY = {
    orgLocationMasterID: "locationName",
};

const SectionTitle = ({ children }) => (
    <Typography
        sx={{
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: FORM_TOKENS.inkLabel,
            mb: 1.5,
            display: "block",
        }}
    >
        {children}
    </Typography>
);

const Card = ({ children }) => (
    <Box
        sx={{
            bgcolor: FORM_TOKENS.surface,
            border: `1px solid ${FORM_TOKENS.cardBorder}`,
            borderRadius: "10px",
            p: 3,
            mb: 3,
        }}
    >
        {children}
    </Box>
);

const Field = ({ label, value, wide }) => (
    <Box sx={{ minWidth: 0, gridColumn: wide ? { sm: "1 / -1" } : undefined }}>
        <Typography
            sx={{ fontSize: "12px", lineHeight: "20px", color: FORM_TOKENS.inkLabel, mb: 0.25 }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: "14px",
                lineHeight: "22px",
                fontWeight: 500,
                color: FORM_TOKENS.ink,
                overflowWrap: "anywhere",
            }}
        >
            {value}
        </Typography>
    </Box>
);

// A linked-records table. Plain markup rather than GenericDraggableTable: there
// is no paging, sorting, search or selection to hang off it, and pulling that
// machinery in would mean wiring five props to reach a read-only list.
const LinkedSection = ({ section, rows }) => {
    const columns = section.columns || [];
    const list = Array.isArray(rows) ? rows : [];

    return (
        <Card>
            <SectionTitle>{section.title || section.key}</SectionTitle>

            {list.length === 0 ? (
                <Typography sx={{ fontSize: "14px", color: FORM_TOKENS.inkFaint }}>
                    {section.emptyMessage || "Nothing linked yet."}
                </Typography>
            ) : (
                <Box sx={{ overflowX: "auto" }}>
                    <Box
                        component="table"
                        sx={{
                            width: "100%",
                            borderCollapse: "collapse",
                            "& th, & td": {
                                textAlign: "left",
                                padding: "10px 12px",
                                borderBottom: `1px solid ${FORM_TOKENS.bandRule}`,
                                whiteSpace: "nowrap",
                            },
                            "& th": {
                                fontSize: "12px",
                                fontWeight: 600,
                                color: FORM_TOKENS.inkLabel,
                            },
                            "& td": { fontSize: "14px", color: FORM_TOKENS.ink },
                        }}
                    >
                        <thead>
                            <tr>
                                {columns.map((c) => (
                                    <th key={c.key}>{c.label || c.key}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((row, index) => (
                                <tr key={row.id ?? index}>
                                    {columns.map((c) => (
                                        <td key={c.key}>
                                            {/* No timestamp treatment for a
                                                date: a YYYY-MM-DD carries no
                                                timezone, and parsing it as UTC
                                                midnight shifts it a day back
                                                for a negative-offset org. */}
                                            {formatCellForCSV(
                                                { dataIndex: c.key, defaultValue: "-" },
                                                row
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                </Box>
            )}
        </Card>
    );
};

const EntityDetailView = ({
    entityType,
    entity,
    sections = [],
    // Back, not close. A page is LEFT, and the label says where for -- see the
    // note on the dialog above.
    onBack,
    backLabel = "Network",
    onEdit,
    // Page meta, for the assets table below -- it builds its columns, filters
    // and searchable fields from the same config the Assets tab does.
    metaInfo = null,
}) => {
    // Seeded from the row so the page paints immediately, then replaced by the
    // detail response -- the row carries only the columns the table shows.
    const [detail, setDetail] = useState(entity);
    const [isLoading, setIsLoading] = useState(false);

    const entityID = entity?.id;

    useEffect(() => {
        setDetail(entity);
    }, [entity]);

    useEffect(() => {
        if (!entityID) return undefined;
        let cancelled = false;
        setIsLoading(true);
        (async () => {
            const res = await getNetworkEntity(entityID);
            if (cancelled) return;
            if (res?.responseStatus?.code === CODE.SUCCESS && res?.data) {
                setDetail(res.data);
            }
            // A failure leaves the row-seeded values on screen rather than
            // blanking a page the user is already reading.
            setIsLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [entityID]);

    const handleEdit = useCallback(() => onEdit?.(detail), [onEdit, detail]);

    if (!entity || !entityType) return null;

    const properties = entityType.properties || [];

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                bgcolor: FORM_TOKENS.panel,
            }}
        >
                <Box
                    sx={{
                        // THE SAME OFFSET THE MODULE SHELL USES
                        // (AssetManagement.jsx). Pinned at 0 the header slides
                        // under the fixed toolbar; these two numbers are the
                        // ones already shipped on the sibling page, so this
                        // lines up with it rather than being measured again.
                        position: "sticky",
                        top: "50px",
                        zIndex: 1000,
                        bgcolor: FORM_TOKENS.panel,
                        px: 3,
                        pt: 2,
                        pb: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                minWidth: 0,
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={onBack}
                                data-testid="network-entity-detail-back"
                                aria-label={`Back to ${backLabel}`}
                            >
                                <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                            <Typography
                                variant="fw700s20lh24c2E2E2E"
                                sx={{ overflowWrap: "anywhere" }}
                            >
                                {detail?.name || entityType.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: FORM_TOKENS.inkFaint,
                                    flexShrink: 0,
                                    ml: 1,
                                }}
                            >
                                {entityType.label}
                                {detail?.entityCode ? ` · ${detail.entityCode}` : ""}
                            </Typography>
                            {isLoading && (
                                <CircularProgress size={14} sx={{ ml: 1.5 }} />
                            )}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {onEdit && (
                                <PrimaryFormActionButton
                                    data-testid="network-entity-detail-edit"
                                    onClick={handleEdit}
                                >
                                    Edit
                                </PrimaryFormActionButton>
                            )}
                        </Box>
                    </Box>

                    <Divider sx={{ mt: 2, borderColor: FORM_TOKENS.fieldBorder }} />
                </Box>

                <Box sx={{ px: 3, pt: 3, pb: 4 }}>
                    <Card>
                        <SectionTitle>Details</SectionTitle>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "1fr 1fr",
                                    lg: "repeat(3, 1fr)",
                                },
                                columnGap: 4,
                                rowGap: 2.5,
                            }}
                        >
                            {properties.map((property) => {
                                const dataIndex =
                                    DISPLAY_KEY[property.key] || property.key;
                                return (
                                    <Field
                                        key={property.key}
                                        label={property.label}
                                        wide={property.type === "textarea"}
                                        value={formatCellForCSV(
                                            { dataIndex, defaultValue: "-" },
                                            detail || {}
                                        )}
                                    />
                                );
                            })}
                        </Box>
                    </Card>

                    {/* WHAT THIS ENTITY IS HOLDING, and the reason anyone
                        opens an entity at all.

                        THE ASSETS TAB'S OWN TABLE, with currentEntityID
                        pinned -- not a config-driven section fed from the
                        entity's detail response. That was the shape this file
                        was built for, and it does not survive contact with a
                        warehouse: embedding the list in the detail payload
                        means no pagination, no search, no sort and no CSV, a
                        second asset serialiser on the backend, and one
                        response carrying five thousand rows. The list
                        endpoint already does all of it, and already takes
                        currentEntityID as a filter.

                        The user cannot clear the pin -- see useAssets. */}
                    {detail?.id ? (
                        <AssetsTabContent
                            metaInfo={metaInfo}
                            canEdit={false}
                            tabSlug="network"
                            tabLabel="Network"
                            pinnedFilters={{ currentEntityID: [detail.id] }}
                            title={heldAssetsHeading(
                                entityType?.code, entityType?.label
                            )}
                            emptyHint={heldAssetsEmptyHint(entityType?.code)}
                        />
                    ) : null}

                    {/* Anything else the org has configured. The assets are
                        no longer one of these -- see above -- so a section
                        here is genuinely other linked data. */}
                    {sections
                        .filter((section) => section.key !== "batteries")
                        .map((section) => (
                            <LinkedSection
                                key={section.key}
                                section={section}
                                rows={detail?.[section.dataKey || section.key]}
                            />
                        ))}

                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <CancelFormActionButton
                            data-testid="network-entity-detail-back-bottom"
                            onClick={onBack}
                        >
                            Back to {backLabel}
                        </CancelFormActionButton>
                    </Box>
                </Box>
        </Box>
    );
};

export default EntityDetailView;
